/**
 * Desktop Authentication Dialog Utility
 * Handles Office.js dialog-based authentication for Desktop Word environments
 * Adapted from your R&D repo for the current codebase
 */

/**
 * Detects if the add-in is running in Office Online (SharePoint) vs Desktop
 * @returns {boolean} true if running in Office Online, false if desktop
 */
const isOfficeOnline = () => {
  try {
    // Check for manual override via query parameter (for testing)
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('forceOnline') === 'true') {
        console.log('[DESKTOP-AUTH-DIALOG] Forced Office Online mode via query parameter');
        return true;
      }
      if (urlParams.get('forceDesktop') === 'true') {
        console.log('[DESKTOP-AUTH-DIALOG] Forced Office Desktop mode via query parameter');
        return false;
      }
    }
    
    // Check if we're in a web environment
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      
      // Check for known Office Online domains
      if (hostname.includes('sharepoint') ||
          hostname.includes('office.com') ||
          hostname.includes('officeapps.live.com') ||
          hostname.includes('outlook.office') ||
          hostname.includes('outlook.live') ||
          hostname.includes('office365.com')) {
        console.log('[DESKTOP-AUTH-DIALOG] Detected Office Online based on hostname:', hostname);
        return true;
      }
    }
    
    // Check Office context if available
    if (typeof Office !== 'undefined' && Office.context && Office.context.platform) {
      const platform = Office.context.platform;
      if (platform === Office.PlatformType.OfficeOnline) {
        console.log('[DESKTOP-AUTH-DIALOG] Detected Office Online based on platform:', platform);
        return true;
      }
    }
    
    // For now, assume localhost testing is desktop unless hostname suggests otherwise
    console.log('[DESKTOP-AUTH-DIALOG] Detected Office Desktop (default)');
    return false;
  } catch (error) {
    console.warn('[DESKTOP-AUTH-DIALOG] Could not detect Office platform, defaulting to desktop approach:', error);
    return false;
  }
};

/**
 * Opens an Office.js authentication dialog and handles login/logout with OIDC.
 * Uses different approaches for Office Online vs Desktop to work around Office Online dialog issues.
 *
 * @param {string} url - The URL to open in the dialog (userAuthentication.html).
 * @param {string} action - "signin" or "signout".
 * @param {function} onSuccess - Called when login/logout succeeds.
 * @param {function} onError - Called when an error occurs.
 */
const showDesktopAuthenticationDialog = (url, action, onSuccess, onError) => {
  if (isOfficeOnline()) {
    console.log('[DESKTOP-AUTH-DIALOG] Detected Office Online - using double jump technique');
    showOfficeOnlineDialog(url, action, onSuccess, onError);
  } else {
    console.log('[DESKTOP-AUTH-DIALOG] Detected Office Desktop - using standard dialog');
    showDesktopDialog(url, action, onSuccess, onError);
  }
};

/**
 * Standard dialog approach for Office Desktop
 */
const showDesktopDialog = (url, action, onSuccess, onError) => {
  let dialog;

  console.log('[DESKTOP-AUTH-DIALOG] Opening desktop dialog:', url);

  Office.context.ui.displayDialogAsync(
    url,
    { height: 60, width: 40, displayInIframe: false },
    (asyncResult) => {
      if (asyncResult.status === "failed") {
        let errorDescription = "";

        switch (asyncResult.error.code) {
          case 12004:
            errorDescription = "Domain is not trusted";
            console.log('[DESKTOP-AUTH-DIALOG] 12004', errorDescription);
            break;
          case 12005:
            errorDescription = "HTTPS is required";
            console.log('[DESKTOP-AUTH-DIALOG] 12005', errorDescription);
            break;
          case 12007:
            errorDescription = "A dialog is already opened.";
            console.log('[DESKTOP-AUTH-DIALOG] 12007', errorDescription);
            break;
          default:
            errorDescription = asyncResult.error.message;
            console.log('[DESKTOP-AUTH-DIALOG] default', errorDescription);
            break;
        }

        console.error('[DESKTOP-AUTH-DIALOG] Dialog failed:', errorDescription);
        if (onError) onError(errorDescription);
        return;
      }

      dialog = asyncResult.value;
      console.log('[DESKTOP-AUTH-DIALOG] ✅ Dialog opened successfully');

      // Handle messages from dialog (Office.context.ui.messageParent)
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (arg) => {
          console.log('[DESKTOP-AUTH-DIALOG] 📨 ====== MESSAGE RECEIVED FROM DIALOG ======');
          console.log('[DESKTOP-AUTH-DIALOG] 📋 Message type:', typeof arg.message);
          console.log('[DESKTOP-AUTH-DIALOG] 📋 Message length:', arg.message?.length || 0);
          console.log('[DESKTOP-AUTH-DIALOG] 📋 Message preview:', arg.message?.substring(0, 300) + '...');
          
          try {
            if (arg.message === "SignOutSuccess") {
              console.log('[DESKTOP-AUTH-DIALOG] 🚪 Sign out message detected');
              setTimeout(() => {
                console.log('[DESKTOP-AUTH-DIALOG] 🗑️ Clearing localStorage for signout');
                localStorage.setItem("IsUserLoggedIn", "false");
                localStorage.removeItem("UserData");
                console.log('[DESKTOP-AUTH-DIALOG] ✅ Signout localStorage updated');
                if (onSuccess) onSuccess(null); // logout success
                dialog.close();
              }, 2000);
            } else if (arg.message.startsWith("ERROR:")) {
              console.log('[DESKTOP-AUTH-DIALOG] ❌ Error message detected:', arg.message);
              const errorText = arg.message.substring(6); // Remove "ERROR:" prefix
              console.log('[DESKTOP-AUTH-DIALOG] ❌ Error details:', errorText);
              
              // Check if this is a critical error that should trigger auto-close with toast
              const isRetriableError = errorText.includes('organizations') || 
                                     errorText.includes('access denied') || 
                                     errorText.includes('unauthorized') ||
                                     errorText.includes('permission') ||
                                     errorText.includes('expired') ||
                                     errorText.includes('invalid');
              
              if (isRetriableError) {
                console.log('[DESKTOP-AUTH-DIALOG] Retriable error - showing toast and auto-closing dialog');
                // Close dialog immediately so user can see the toast message
                dialog.close();
                // Call error handler after a brief delay to ensure dialog closes first
                setTimeout(() => {
                  if (onError) onError(errorText);
                }, 100);
              } else {
                console.log('[DESKTOP-AUTH-DIALOG] Non-retriable error - closing dialog');
                dialog.close();
                if (onError) onError(errorText);
              }
            } else {
              console.log('[DESKTOP-AUTH-DIALOG] 🔐 Login message detected - parsing JSON...');
              const msg = JSON.parse(arg.message);
              
              console.log('[DESKTOP-AUTH-DIALOG] 📋 Parsed message structure:', {
                hasResponse: !!msg.response,
                hasCode: !!msg.code,
                responseType: typeof msg.response,
                responseKeys: msg.response ? Object.keys(msg.response) : []
              });
              
              if (msg.response) {
                console.log('[DESKTOP-AUTH-DIALOG] 📋 User data in response:', {
                  hasProfile: !!msg.response.profile,
                  email: msg.response.profile?.email,
                  hasAccessToken: !!msg.response.access_token,
                  expiresAt: msg.response.expires_at
                });
              }
              
              console.log('[DESKTOP-AUTH-DIALOG] 💾 Setting localStorage...');
              localStorage.setItem("IsUserLoggedIn", "true");
              localStorage.setItem("UserData", JSON.stringify(msg.response));
              
              // Verify localStorage was set
              const storedLogin = localStorage.getItem("IsUserLoggedIn");
              const storedData = localStorage.getItem("UserData");
              console.log('[DESKTOP-AUTH-DIALOG] ✅ localStorage verification:');
              console.log('[DESKTOP-AUTH-DIALOG] - IsUserLoggedIn:', storedLogin);
              console.log('[DESKTOP-AUTH-DIALOG] - UserData exists:', !!storedData);
              console.log('[DESKTOP-AUTH-DIALOG] - UserData length:', storedData?.length || 0);
              
              console.log('[DESKTOP-AUTH-DIALOG] 🎯 Calling success callback');
              if (onSuccess) onSuccess(msg.response); // login success
              
              console.log('[DESKTOP-AUTH-DIALOG] 🚪 Closing dialog');
              dialog.close();
            }
          } catch (err) {
            console.error('[DESKTOP-AUTH-DIALOG] ❌ Message handler error:', err);
            console.error('[DESKTOP-AUTH-DIALOG] ❌ Error stack:', err.stack);
            console.error('[DESKTOP-AUTH-DIALOG] ❌ Failed message:', arg.message);
            if (onError) onError(err.message);
            dialog.close();
          }
        }
      );

      // Handle dialog close or error
      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (arg) => {
          let errorDescription = "";

          switch (arg.error) {
            case 12002:
              errorDescription = "Cannot load URL, no such page or bad URL syntax.";
              console.log('[DESKTOP-AUTH-DIALOG] 12002', errorDescription);
              break;
            case 12003:
              errorDescription = "HTTPS is required.";
              console.log('[DESKTOP-AUTH-DIALOG] 12003', errorDescription);
              break;
            case 12006:
              errorDescription = "Dialog closed by user.";
              console.log('[DESKTOP-AUTH-DIALOG] 12006', errorDescription);
              break;
            default:
              errorDescription = "Undefined error in dialog window.";
              console.log('[DESKTOP-AUTH-DIALOG] default', errorDescription);
              break;
          }

          console.error('[DESKTOP-AUTH-DIALOG] Dialog event:', errorDescription);
          if (onError) onError(errorDescription);
        }
      );
    }
  );
};

/**
 * Double jump dialog approach for Office Online (SharePoint)
 * Uses a two-dialog system to work around Office Online communication issues
 */
const showOfficeOnlineDialog = (url, action, onSuccess, onError) => {
  // For Office Online, we use the double jump approach
  // First dialog will be our intermediary that never redirects
  const doubleJumpUrl = `${window.location.origin}/login/double-jump.html?originalUrl=${encodeURIComponent(url)}&action=${action}`;
  
  let dialog;

  console.log('[DESKTOP-AUTH-DIALOG] Opening Office Online double jump dialog:', doubleJumpUrl);

  Office.context.ui.displayDialogAsync(
    doubleJumpUrl,
    { height: 60, width: 40, displayInIframe: false },
    (asyncResult) => {
      if (asyncResult.status === "failed") {
        let errorDescription = "";

        switch (asyncResult.error.code) {
          case 12004:
            errorDescription = "Domain is not trusted";
            break;
          case 12005:
            errorDescription = "HTTPS is required";
            break;
          case 12007:
            errorDescription = "A dialog is already opened.";
            break;
          default:
            errorDescription = asyncResult.error.message;
            break;
        }

        console.error('[DESKTOP-AUTH-DIALOG] Double jump dialog failed:', errorDescription);
        if (onError) onError(errorDescription);
        return;
      }

      dialog = asyncResult.value;
      console.log('[DESKTOP-AUTH-DIALOG] ✅ Double jump dialog opened successfully');

      // Handle messages from the double jump dialog
      dialog.addEventHandler(
        Office.EventType.DialogMessageReceived,
        (arg) => {
          console.log('[DESKTOP-AUTH-DIALOG] 🎯 ====== MAIN DIALOG MESSAGE RECEIVED ======');
          console.log('[DESKTOP-AUTH-DIALOG] 📨 Message received from double-jump dialog');
          console.log('[DESKTOP-AUTH-DIALOG] 📋 Message type:', typeof arg.message);
          console.log('[DESKTOP-AUTH-DIALOG] 📋 Message content:', arg.message);
          try {
            if (arg.message === "SignOutSuccess") {
              console.log('[DESKTOP-AUTH-DIALOG] Double jump sign out successful');
              setTimeout(() => {
                localStorage.setItem("IsUserLoggedIn", "false");
                localStorage.removeItem("UserData");
                if (onSuccess) onSuccess(null);
                dialog.close();
              }, 2000);
            } else if (arg.message === "FORCE_CLOSE") {
              console.log('[DESKTOP-AUTH-DIALOG] 🔴 Force close message received - closing dialog immediately');
              if (dialog) {
                dialog.close();
              }
            } else if (arg.message.startsWith("ERROR:")) {
              console.log('[DESKTOP-AUTH-DIALOG] ❌ SharePoint error message received:', arg.message);
              const errorText = arg.message.substring(6); // Remove "ERROR:" prefix
              
              // Check if this is a critical error that should trigger auto-close with toast
              const isRetriableError = errorText.includes('organizations') || 
                                     errorText.includes('access denied') || 
                                     errorText.includes('unauthorized') ||
                                     errorText.includes('permission') ||
                                     errorText.includes('expired') ||
                                     errorText.includes('invalid');
              
              if (isRetriableError) {
                console.log('[DESKTOP-AUTH-DIALOG] Retriable SharePoint error - showing toast and auto-closing dialog');
                // Close dialog immediately so user can see the toast message
                if (dialog) { dialog.close(); }
                // Call error handler after a brief delay to ensure dialog closes first
                setTimeout(() => {
                  if (onError) onError(errorText);
                }, 100);
              } else {
                console.log('[DESKTOP-AUTH-DIALOG] Non-retriable SharePoint error - closing dialog');
                if (dialog) { dialog.close(); }
                if (onError) onError(errorText);
              }
            } else {
              console.log('[DESKTOP-AUTH-DIALOG] 🔐 SharePoint login - parsing JSON...');
              const msg = JSON.parse(arg.message);
              console.log('[DESKTOP-AUTH-DIALOG] ✅ SharePoint sign in successful:', {
                hasResponse: !!msg.response,
                userEmail: msg.response?.profile?.email
              });
              
              localStorage.setItem("IsUserLoggedIn", "true");
              localStorage.setItem("UserData", JSON.stringify(msg.response));
              
              console.log('[DESKTOP-AUTH-DIALOG] 💾 SharePoint localStorage updated');
              if (onSuccess) onSuccess(msg.response);
              
              // Close double-jump dialog after successful authentication with a brief delay
              console.log('[DESKTOP-AUTH-DIALOG] ✅ Authentication successful - closing dialog in 2 seconds...');
              console.log('[DESKTOP-AUTH-DIALOG] 🕐 Dialog reference check:', !!dialog);
              setTimeout(() => {
                console.log('[DESKTOP-AUTH-DIALOG] 🎉 Attempting to close double-jump dialog...');
                if (dialog) {
                  try {
                    dialog.close();
                    console.log('[DESKTOP-AUTH-DIALOG] ✅ Double-jump dialog closed successfully');
                  } catch (error) {
                    console.error('[DESKTOP-AUTH-DIALOG] ❌ Error closing dialog:', error);
                  }
                } else {
                  console.error('[DESKTOP-AUTH-DIALOG] ❌ Dialog reference is null - cannot close');
                }
              }, 2000); // 2 second delay to show success
            }
          } catch (err) {
            console.error('[DESKTOP-AUTH-DIALOG] Double jump message handler error:', err);
            if (onError) onError(err.message);
            // dialog.close(); // DEBUG: Temporarily disabled to prevent premature closing
          }
        }
      );

      // Handle dialog close or error
      dialog.addEventHandler(
        Office.EventType.DialogEventReceived,
        (arg) => {
          let errorDescription = "";

          switch (arg.error) {
            case 12002:
              errorDescription = "Cannot load URL, no such page or bad URL syntax.";
              break;
            case 12003:
              errorDescription = "HTTPS is required.";
              break;
            case 12006:
              errorDescription = "Dialog closed by user.";
              break;
            default:
              errorDescription = "Undefined error in dialog window.";
              break;
          }

          console.error('[DESKTOP-AUTH-DIALOG] Double jump dialog event:', errorDescription);
          if (onError) onError(errorDescription);
        }
      );
    }
  );
};

/**
 * Check if user is already logged in (from localStorage)
 * @returns {Object|null} - User data if logged in, null if not
 */
const getCurrentUser = () => {
  try {
    const isLoggedIn = localStorage.getItem("IsUserLoggedIn") === "true";
    if (isLoggedIn) {
      const userData = localStorage.getItem("UserData");
      return userData ? JSON.parse(userData) : null;
    }
    return null;
  } catch (error) {
    console.error('[DESKTOP-AUTH-DIALOG] Error getting current user:', error);
    return null;
  }
};

/**
 * Clear user authentication data
 */
const clearUserData = () => {
  console.log('[DESKTOP-AUTH-DIALOG] Clearing user data');
  localStorage.removeItem("IsUserLoggedIn");
  localStorage.removeItem("UserData");
};

export {
  showDesktopAuthenticationDialog,
  getCurrentUser,
  clearUserData,
  isOfficeOnline
};

export default showDesktopAuthenticationDialog;
